/**
 * QuickPlaceGrid Component
 * Displays category quick-access buttons for the Unified Placement Palette
 * Icons are fetched dynamically from category templates with Lucide fallbacks
 */

import React, { useMemo, useCallback } from 'react';
import { Lightbulb, SquareEqual, Speaker, Camera, Tv, MoreHorizontal, Wifi } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { sanitizeSVG } from '@/lib/sanitize';
import { unescapeSvg, resizeSvg } from '@/lib/svgNormalizer';
import { useMergedCategories } from '@/features/product-library/hooks/useMergedCategories';
import { categoryTemplateService } from '@/services/categoryTemplateService';
import type { PlacementItem, PlacementItemType } from '../hooks/useUnifiedPlacement';

export interface QuickPlaceCategory {
  type: PlacementItemType;
  category: string;
  subcategory?: string;
  displayName: string;
  fallbackIcon: React.ReactNode;
  color: string;
}

// Uniform dark gray color for all icons in the palette
const PALETTE_ICON_COLOR = '#4B5563';
const PALETTE_ICON_SIZE = 36;

// Quick place category definitions with fallback icons
const QUICK_PLACE_DEFINITIONS: QuickPlaceCategory[] = [
  {
    type: 'fixture',
    category: 'recessed',
    displayName: 'Fixture',
    fallbackIcon: <Lightbulb className="h-9 w-9" />,
    color: PALETTE_ICON_COLOR,
  },
  {
    type: 'control',
    category: 'control',
    subcategory: 'keypad',
    displayName: 'Keypad',
    fallbackIcon: <SquareEqual className="h-9 w-9" />,
    color: PALETTE_ICON_COLOR,
  },
  {
    type: 'product',
    category: 'speaker',
    subcategory: 'in-ceiling',
    displayName: 'Speaker',
    fallbackIcon: <Speaker className="h-9 w-9" />,
    color: PALETTE_ICON_COLOR,
  },
  {
    type: 'product',
    category: 'camera',
    subcategory: 'dome',
    displayName: 'Camera',
    fallbackIcon: <Camera className="h-9 w-9" />,
    color: PALETTE_ICON_COLOR,
  },
  {
    type: 'product',
    category: 'access_point',
    subcategory: 'Indoor Access Point',
    displayName: 'WiFi AP',
    fallbackIcon: <Wifi className="h-9 w-9" />,
    color: PALETTE_ICON_COLOR,
  },
  {
    type: 'product',
    category: 'Display',
    subcategory: 'TV',
    displayName: 'TV',
    fallbackIcon: <Tv className="h-9 w-9" />,
    color: PALETTE_ICON_COLOR,
  },
];

interface QuickPlaceGridProps {
  onSelect: (item: PlacementItem) => void;
  className?: string;
  showMore?: boolean;
  onMoreClick?: () => void;
}

export function QuickPlaceGrid({
  onSelect,
  className,
  showMore = false,
  onMoreClick,
}: QuickPlaceGridProps) {
  // Fetch category templates for icons
  const { data: lvCategories = [] } = useMergedCategories('lv_products');

  const { data: fixtureTemplates = [] } = useQuery({
    queryKey: ['category-templates', 'lighting_fixtures'],
    queryFn: () => categoryTemplateService.getByDomain('lighting_fixtures'),
    staleTime: 5 * 60 * 1000,
  });

  const { data: controlTemplates = [] } = useQuery({
    queryKey: ['category-templates', 'lighting_controls'],
    queryFn: () => categoryTemplateService.getByDomain('lighting_controls'),
    staleTime: 5 * 60 * 1000,
  });

  // Helper to find template icon for a category
  const getTemplateIcon = useCallback(
    (type: PlacementItemType, category: string, subcategory?: string): string | null => {
      // Normalize category name (handle both hyphens and underscores)
      const normalizedCategory = category.toLowerCase().replace(/-/g, '_');

      let templates: Array<{
        category: string;
        subcategory?: string | null;
        icon_svg?: string | null;
      }> = [];

      if (type === 'fixture') {
        templates = fixtureTemplates;
      } else if (type === 'control') {
        templates = controlTemplates;
      } else {
        templates = lvCategories;
      }

      // Find matching template
      const template = templates.find(t => {
        const templateCategory = t.category.toLowerCase().replace(/-/g, '_');
        const categoryMatch = templateCategory === normalizedCategory;

        if (!subcategory) {
          // If no subcategory specified, match any template in this category
          return categoryMatch;
        }

        // Match both category and subcategory
        const templateSubcategory = t.subcategory?.toLowerCase().replace(/-/g, '_');
        return (
          categoryMatch && templateSubcategory === subcategory.toLowerCase().replace(/-/g, '_')
        );
      });

      return template?.icon_svg || null;
    },
    [fixtureTemplates, controlTemplates, lvCategories],
  );

  // Build quick categories with dynamic icons
  const quickCategories = useMemo(() => {
    return QUICK_PLACE_DEFINITIONS.map(def => ({
      ...def,
      templateIcon: getTemplateIcon(def.type, def.category, def.subcategory),
    }));
  }, [getTemplateIcon]);

  const handleCategoryClick = useCallback(
    (category: QuickPlaceCategory & { templateIcon: string | null }) => {
      const item: PlacementItem = {
        type: category.type,
        category: category.category,
        subcategory: category.subcategory,
        displayName: category.displayName,
        iconSvg: category.templateIcon || undefined,
        iconColor: category.color,
      };
      onSelect(item);
    },
    [onSelect],
  );

  // Render icon - use template SVG if available, otherwise fallback to Lucide icon
  const renderIcon = useCallback(
    (templateIcon: string | null, fallbackIcon: React.ReactNode, _color: string) => {
      if (templateIcon) {
        // Clean and resize the SVG - normalization happens at upload time
        const cleanedSvg = unescapeSvg(templateIcon);
        const resizedSvg = resizeSvg(cleanedSvg, PALETTE_ICON_SIZE, PALETTE_ICON_SIZE);
        const sanitizedSvg = sanitizeSVG(resizedSvg);
        return (
          <div
            className="flex items-center justify-center"
            style={{ color: PALETTE_ICON_COLOR }} // Uniform dark gray for all icons
            dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
          />
        );
      }
      return fallbackIcon;
    },
    [],
  );

  return (
    <div className={cn('grid grid-cols-6 gap-2', className)}>
      {quickCategories.map((category, index) => (
        <Button
          key={`${category.type}-${category.category}-${index}`}
          variant="outline"
          className={cn(
            'flex flex-col items-center justify-center h-16 w-full p-1.5',
            'hover:bg-accent hover:border-accent-foreground/20',
            'transition-colors duration-150',
          )}
          onClick={() => handleCategoryClick(category)}
        >
          <div className="flex items-center justify-center" style={{ color: PALETTE_ICON_COLOR }}>
            {renderIcon(category.templateIcon, category.fallbackIcon, category.color)}
          </div>
          <span className="text-[10px] font-medium text-muted-foreground truncate w-full text-center mt-0.5">
            {category.displayName}
          </span>
        </Button>
      ))}

      {showMore && (
        <Button
          variant="outline"
          className={cn(
            'flex flex-col items-center justify-center h-16 w-full p-1.5',
            'hover:bg-accent hover:border-accent-foreground/20',
            'transition-colors duration-150',
          )}
          onClick={onMoreClick}
        >
          <div className="flex items-center justify-center" style={{ color: PALETTE_ICON_COLOR }}>
            <MoreHorizontal className="h-9 w-9" />
          </div>
          <span className="text-[10px] font-medium text-muted-foreground mt-0.5">More</span>
        </Button>
      )}
    </div>
  );
}

export default QuickPlaceGrid;
